import { clsx } from 'clsx';
import type { MouseEvent, ReactElement } from 'react';
import { Children, cloneElement } from 'react';

import type { ReeferBaseProps } from '../../utils/baseProps.types';
import type {
  ButtonToggleButtonProps,
  SupportedValue,
} from './buttonToggleButton/buttonToggleButton';
import { ButtonToggleButton } from './buttonToggleButton/buttonToggleButton';

import styles from './buttonToggle.module.css';

export interface ButtonToggleProps<
  T extends SupportedValue = SupportedValue,
> extends ReeferBaseProps {
  /** Aria label */
  ariaLabel?: string;

  /** Only allows `ButtonToggle.Button` components as children */
  children: ReactElement<ButtonToggleButtonProps<T>>[];

  /** Stretches the container to fit its parent; buttons positioned with space-between */
  full?: boolean;

  /** `onChange` handler, returns the value of the clicked button */
  onChange: (value: T) => void;

  /** The currently selected value */
  value: T;

  /** Set the variant of button to display */
  variant?: 'primary' | 'inverse' | 'tertiary';
}

/**
 * The `ButtonToggle` component allows the user to toggle between a series of options.
 * An `onChange` handler will be fired when the selected option is changed.
 */
function ButtonToggleBase<T extends SupportedValue = SupportedValue>({
  ariaLabel,
  children,
  className,
  'data-testid': testId,
  full = true,
  id,
  onChange,
  style,
  value,
  variant = 'primary',
}: ButtonToggleProps<T>) {
  return (
    <div
      aria-label={ariaLabel}
      className={clsx(className, styles['buttonToggle'], {
        [styles['buttonToggle__full']]: full,
      })}
      data-testid={testId}
      id={id}
      style={style}
    >
      {Children.map(children, (child) =>
        cloneElement(child, {
          full,
          onClick: (e: MouseEvent<HTMLButtonElement>) => {
            child.props?.onClick && child.props.onClick(e);
            onChange(child.props.value);
          },
          selected: child.props.value === value,
          variant,
        })
      )}
    </div>
  );
}

export const ButtonToggle = Object.assign(ButtonToggleBase, {
  Button: ButtonToggleButton,
});
